import React, { useState, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Form, Container, Row, Col, Alert, Card, Spinner } from 'react-bootstrap';
import { publicApi } from '../api/client';

/**
 * Converts a backend retry time in seconds into a short user-facing duration.
 */
const formatRetryDuration = (seconds) => {
  const retrySeconds = Number(seconds);

  if (!Number.isFinite(retrySeconds) || retrySeconds <= 0) {
    return 'a short while';
  }

  const minutes = Math.floor(retrySeconds / 60);
  const remainingSeconds = retrySeconds % 60;

  if (minutes > 0 && remainingSeconds > 0) {
    return `${minutes} minute${minutes === 1 ? '' : 's'} and ${remainingSeconds} second${remainingSeconds === 1 ? '' : 's'}`;
  }

  if (minutes > 0) {
    return `${minutes} minute${minutes === 1 ? '' : 's'}`;
  }

  return `${remainingSeconds} second${remainingSeconds === 1 ? '' : 's'}`;
};

/**
 * Extracts a readable authentication error from FastAPI responses.
 * FastAPI can return detail as a string or as a validation error array.
 */
const getAuthErrorMessage = (error) => {
  const detail = error.response?.data?.detail;
  const status = error.response?.status;

  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    // Prefer backend error codes so lockout and rate-limit messages stay distinct.
    if (detail.code === 'ACCOUNT_LOCKED') {
      return {
        title: 'Account temporarily locked',
        message: `Too many unsuccessful login attempts. Please try again in ${formatRetryDuration(detail.retry_after_seconds)}.`,
      };
    }

    if (detail.code === 'RATE_LIMITED') {
      return {
        title: 'Too many login attempts',
        message: `Please wait ${formatRetryDuration(detail.retry_after_seconds)} before trying to log in again.`,
      };
    }

    return {
      title: 'Login failed',
      message: detail.message || 'Please check your details and try again.',
    };
  }

  if (typeof detail === 'string') {
    const lockoutMatch = detail.match(/Account locked\. Try again in (\d+) seconds\.?/i);

    // Format account lockout responses without exposing the raw backend message.
    if ((status === 423 || status === 429) && lockoutMatch) {
      return {
        title: 'Account temporarily locked',
        message: `Too many unsuccessful login attempts. Please try again in ${formatRetryDuration(lockoutMatch[1])}.`,
      };
    }

    // Format generic rate-limit responses into a calmer login-specific message.
    if (status === 429) {
      return {
        title: 'Too many login attempts',
        message: 'Please wait a moment before trying to log in again.',
      };
    }

    return {
      title: 'Login failed',
      message: detail,
    };
  }

  if (Array.isArray(detail) && detail.length > 0) {
    return {
      title: 'Login failed',
      message: detail.map((item) => item.msg).filter(Boolean).join(' ') || 'Please check your details and try again.',
    };
  }

  return {
    title: status === 429 ? 'Too many login attempts' : 'Login failed',
    message: status === 429
      ? 'Please wait a moment before trying to log in again.'
      : error.response?.data?.message || 'Unable to log in. Please check your email and password.',
  };
};

/**
 * LoginPage Component - A form for users to log in to the application.
 * This component handles the login process, including form submission,
 * validation, error handling, and displaying lockout information if applicable.
 *
 * @component
 * @example
 * return (
 *   <LoginPage />
 * )
 *
 * @param {Object} props - The component props (currently no props are passed).
 * @param {function} login - Function for logging in the user (from context).
 * @param {function} navigate - Function for navigating to different routes (from react-router-dom).
 */
const LoginPage = () => {
  // State hooks to manage form inputs and error handling
  const [email, setEmail] = useState(''); // Store email state
  const [password, setPassword] = useState(''); // Store password state
  const [error, setError] = useState(null); // Store error messages
  const [isSubmitting, setIsSubmitting] = useState(false); // State to track login submission

  const { login } = useContext(AuthContext); // Use the login function from AuthContext to authenticate
  const navigate = useNavigate() // Hook to navigate to different pages (after successful login)

  /**
   * Handles form submission for logging in the user.
   * Sends login credentials (email and password) to the backend API.
   * If the credentials are valid, stores the access and refresh tokens in localStorage
   * and redirects the user to the home page. If there is an error (e.g., invalid credentials
   * or locked account), displays an appropriate error message to the user.
   *
   * @param {React.FormEvent} e - The form submission event.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      // Send login credentials to the FastAPI auth endpoint without triggering token refresh.
      const response = await publicApi.post('/api/v1/auth/login', {
        email,
        password,
      });

      // Check if the response contains the JWT tokens and log the user in
      if (response.data.access_token && response.data.refresh_token) {
        // Store JWT tokens in localStorage
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);

        // Call the login function from context to update the app state
        login(response.data.access_token, email);

        // Navigate to the home page upon successful login
        navigate('/');
      }
    } catch (err) {
       // Handle errors if the login attempt fails
      setError(getAuthErrorMessage(err));

      // Clear the password input after a failed login attempt
      setPassword('');
    } finally {
      setIsSubmitting(false);
    }
  };
    
  return (
    // Main container to center the login form on the screen
    <Container className="d-flex justify-content-center align-items-center min-vh-100">
      <Row className="w-100">
        <Col md={6} lg={4} className="mx-auto">
          {/* Card component for displaying the login form */}
          <Card className="shadow-sm">
            <Card.Body>
              {/* Logo and heading */}
              <div className="text-center mb-4">
                <img
                  alt="AGRISMART logo"
                  src="/android-chrome-192x192.png"
                  width="60"
                  height="60"
                  className="text-primary"
                />
                <h2 className="mt-2">Log in</h2>
              </div>

              {/* Form for email and password */}
              <Form onSubmit={handleSubmit}>
                {/* Email input field */}
                <Form.Group controlId="email" className="mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    placeholder="Enter email"
                  />
                </Form.Group>

                {/* Password input field */}
                <Form.Group controlId="password" className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    placeholder="Enter password"
                  />
                </Form.Group>

                {/* Display error message if any */}
                {error && (
                  <Alert variant="danger">
                    <strong className="d-block mb-1">{error.title}</strong>
                    <span>{error.message}</span>
                  </Alert>
                )}

                {/* Submit button */}
                <Button variant="primary" type="submit" className="w-100" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Logging in
                    </>
                  ) : (
                    'Login'
                  )}
                </Button>

                {/* Forgot password navigation */}
                <div className="text-center mt-3">
                  <Button as={Link} to="/forgot-password" variant="link" className="p-0">
                    Forgot password?
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default LoginPage;
